import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { User, Bot, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/utils';
import type { Message } from '@/types';

interface ChatMessageProps {
  message: Message;
  children?: React.ReactNode;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  children,
}) => {
  const isUser = message.sender === 'user';

  return (
    <div className={cn(
      'flex gap-4 animate-slide-up',
      isUser ? 'flex-row-reverse' : 'flex-row'
    )}>
      {/* Enhanced Avatar */}
      <div className="flex-shrink-0">
        <div className={cn(
          'relative w-10 h-10 rounded-xl flex items-center justify-center shadow-md',
          isUser 
            ? 'bg-gradient-to-br from-primary-600 to-primary-700' 
            : 'bg-gradient-to-br from-secondary-100 to-secondary-200 border border-secondary-300/50'
        )}>
          {isUser ? (
            <User className="w-5 h-5 text-white" />
          ) : (
            <Bot className="w-5 h-5 text-secondary-600" />
          )}
          {!isUser && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-success-400 to-success-500 rounded-full flex items-center justify-center">
              <Sparkles className="w-2 h-2 text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Message Content */}
      <div className={cn(
        'flex-1 max-w-[85%] space-y-3',
        isUser ? 'items-end' : 'items-start'
      )}>
        <div className={cn(
          'relative rounded-2xl px-5 py-4 shadow-sm border backdrop-blur-sm',
          isUser
            ? 'bg-gradient-to-br from-primary-600 to-primary-700 text-white ml-auto border-primary-500/20'
            : 'bg-white/90 border-secondary-200/50 text-secondary-900'
        )}>
          {/* Message bubble tail */}
          <div className={cn(
            'absolute top-4 w-3 h-3 rotate-45',
            isUser 
              ? '-right-1.5 bg-gradient-to-br from-primary-600 to-primary-700'
              : '-left-1.5 bg-white/90 border-l border-b border-secondary-200/50'
          )}></div>

          <div className={cn(
            'prose prose-sm max-w-none',
            isUser 
              ? 'prose-invert prose-headings:text-white prose-p:text-white/95 prose-strong:text-white prose-code:text-primary-100' 
              : 'prose-secondary prose-headings:text-secondary-900 prose-p:text-secondary-800'
          )}>
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                // Custom styling for markdown elements
                p: ({ children }) => <p className="mb-3 last:mb-0 leading-relaxed">{children}</p>,
                code: ({ children, className }) => (
                  <code className={cn(
                    'px-2 py-1 rounded-md text-xs font-mono font-medium',
                    isUser 
                      ? 'bg-primary-800/50 text-primary-100 border border-primary-500/30' 
                      : 'bg-secondary-100 text-secondary-800 border border-secondary-200',
                    className
                  )}>
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre className={cn(
                    'p-4 rounded-lg text-xs font-mono overflow-x-auto border',
                    isUser 
                      ? 'bg-primary-800/50 text-primary-100 border-primary-500/30' 
                      : 'bg-secondary-50 text-secondary-800 border-secondary-200'
                  )}>
                    {children}
                  </pre>
                ),
                h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                h2: ({ children }) => <h2 className="text-base font-semibold mb-2">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sm font-semibold mb-1">{children}</h3>,
                ul: ({ children }) => <ul className="space-y-1 ml-4">{children}</ul>,
                ol: ({ children }) => <ol className="space-y-1 ml-4">{children}</ol>,
                li: ({ children }) => <li className="text-sm">{children}</li>,
              }}
            >
              {message.text}
            </ReactMarkdown>
          </div>
        </div>

        {/* Timestamp */}
        <div className={cn(
          'text-xs text-secondary-500 px-2 font-medium',
          isUser ? 'text-right' : 'text-left'
        )}>
          {formatDate(message.timestamp)}
        </div>

        {/* Additional content (charts, tables, etc.) */}
        {children && (
          <div className={cn(
            'mt-4 space-y-4',
            isUser ? 'ml-auto' : 'mr-auto'
          )}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
};